import React, { useState, useEffect } from 'react';
import { 
    Plus, Save, Trash2, GripVertical, ChevronDown, ChevronUp,
    Brain, Heart, Users, Sparkles, Lightbulb, AlertCircle, Shield, 
    Zap, Moon, Activity, HeartHandshake, RefreshCw, Eye, EyeOff
} from 'lucide-react';
import { API_URL } from '../constants';

// Icon mapping for the educational topics
const ICON_MAP = {
    Brain, Heart, Users, Sparkles, Lightbulb, AlertCircle, Shield,
    Zap, Moon, Activity, HeartHandshake, RefreshCw
};

const VARIANT_OPTIONS = [
    { value: 'sage', label: 'Sage (Green)', color: '#7D8E95' },
    { value: 'lavender', label: 'Lavender (Purple)', color: '#7D8E95' },
    { value: 'rose', label: 'Rose (Pink)', color: '#FFBB98' },
    { value: 'blue', label: 'Blue', color: '#7D8E95' }
];

const ICON_OPTIONS = Object.keys(ICON_MAP);

export const EducationTab = () => {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [expandedTopic, setExpandedTopic] = useState(null);
    const [editingTopic, setEditingTopic] = useState(null);

    useEffect(() => {
        fetchContent();
    }, []);

    const fetchContent = async () => {
        try {
            const res = await fetch(`${API_URL}/api/educational/topics`);
            if (res.ok) {
                const data = await res.json();
                setContent(data);
            }
        } catch (error) {
            console.error('Error fetching educational content:', error);
        } finally {
            setLoading(false);
        }
    };

    const saveContent = async () => {
        setSaving(true);
        try {
            const res = await fetch(`${API_URL}/api/educational/topics`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(content)
            });
            if (res.ok) {
                alert('Educational content saved successfully!');
            }
        } catch (error) {
            console.error('Error saving:', error);
            alert('Failed to save content');
        } finally {
            setSaving(false);
        }
    };

    const updateSectionMeta = (field, value) => {
        setContent(prev => ({ ...prev, [field]: value }));
    };

    const updateTopic = (topicId, field, value) => {
        setContent(prev => ({
            ...prev,
            topics: prev.topics.map(t => 
                t.id === topicId ? { ...t, [field]: value } : t
            )
        }));
    };

    const addNewTopic = () => {
        const newId = `topic-${Date.now()}`;
        const newTopic = {
            id: newId,
            icon: 'Brain',
            title: 'New Topic',
            shortAnswer: 'Brief description...',
            fullContent: 'Full content goes here...',
            variant: 'sage',
            order: content.topics.length + 1,
            is_visible: true
        };
        setContent(prev => ({
            ...prev,
            topics: [...prev.topics, newTopic]
        }));
        setExpandedTopic(newId);
        setEditingTopic(newId);
    };

    const deleteTopic = (topicId) => {
        if (window.confirm('Are you sure you want to delete this topic?')) {
            setContent(prev => ({
                ...prev,
                topics: prev.topics.filter(t => t.id !== topicId)
            }));
        }
    };

    const moveTopic = (index, direction) => {
        const newTopics = [...content.topics];
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= newTopics.length) return;
        
        [newTopics[index], newTopics[newIndex]] = [newTopics[newIndex], newTopics[index]];
        newTopics.forEach((t, i) => t.order = i + 1);
        
        setContent(prev => ({ ...prev, topics: newTopics }));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D6B6B]"></div>
            </div>
        );
    }

    if (!content) {
        return (
            <div className="text-center py-12 text-[#7D8E95]">
                Failed to load educational content
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Section Header Settings */}
            <div className="bg-white rounded-xl border border-[#FFBB98] p-6">
                <h3 className="text-lg font-medium text-[#1D6B6B] mb-4">Section Header</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-[#2A8585] mb-1">
                            Section Title
                        </label>
                        <input
                            type="text"
                            value={content.section_title}
                            onChange={(e) => updateSectionMeta('section_title', e.target.value)}
                            className="w-full px-3 py-2 border border-[#FFBB98] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-[#2A8585] mb-1">
                            Section Subtitle
                        </label>
                        <input
                            type="text"
                            value={content.section_subtitle}
                            onChange={(e) => updateSectionMeta('section_subtitle', e.target.value)}
                            className="w-full px-3 py-2 border border-[#FFBB98] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30"
                        />
                    </div>
                </div>
            </div>

            {/* Topics List */}
            <div className="bg-white rounded-xl border border-[#FFBB98] p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-[#1D6B6B]">
                        Educational Topics ({content.topics.length})
                    </h3>
                    <button
                        onClick={addNewTopic}
                        className="flex items-center gap-2 px-4 py-2 bg-[#1D6B6B] text-white rounded-lg hover:bg-[#1D6B6B] transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        Add Topic
                    </button>
                </div>

                <div className="space-y-3">
                    {content.topics
                        .sort((a, b) => a.order - b.order)
                        .map((topic, index) => {
                            const IconComponent = ICON_MAP[topic.icon] || Brain;
                            const isExpanded = expandedTopic === topic.id;
                            const variantStyle = VARIANT_OPTIONS.find(v => v.value === topic.variant);

                            return (
                                <div
                                    key={topic.id}
                                    className={`border rounded-xl overflow-hidden transition-all ${
                                        isExpanded ? 'border-[#1D6B6B]' : 'border-[#FFBB98]'
                                    }`}
                                >
                                    {/* Topic Header */}
                                    <div 
                                        className="flex items-center gap-3 p-4 cursor-pointer hover:bg-[#FEF6EF]"
                                        onClick={() => setExpandedTopic(isExpanded ? null : topic.id)}
                                    >
                                        <div className="flex flex-col gap-1">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); moveTopic(index, 'up'); }}
                                                disabled={index === 0}
                                                className="p-1 hover:bg-[#FFBB98] rounded disabled:opacity-30"
                                            >
                                                <ChevronUp className="w-3 h-3" />
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); moveTopic(index, 'down'); }}
                                                disabled={index === content.topics.length - 1}
                                                className="p-1 hover:bg-[#FFBB98] rounded disabled:opacity-30"
                                            >
                                                <ChevronDown className="w-3 h-3" />
                                            </button>
                                        </div>
                                        
                                        <div 
                                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                                            style={{ backgroundColor: variantStyle?.color || '#FFBB98' }}
                                        >
                                            <IconComponent className="w-5 h-5 text-[#1D6B6B]" />
                                        </div>
                                        
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-[#1D6B6B] truncate">{topic.title}</p>
                                            <p className="text-sm text-[#7D8E95] truncate">{topic.shortAnswer}</p>
                                        </div>
                                        
                                        <div className="flex items-center gap-2">
                                            {!topic.is_visible && (
                                                <span className="px-2 py-1 bg-[#FFBB98] text-[#7D8E95] text-xs rounded">
                                                    Hidden
                                                </span>
                                            )}
                                            <button
                                                onClick={(e) => { e.stopPropagation(); deleteTopic(topic.id); }}
                                                className="p-2 hover:bg-[#FFBB98] rounded-lg text-[#7D8E95] hover:text-[#8B6A6A]"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                            {isExpanded ? (
                                                <ChevronUp className="w-5 h-5 text-[#7D8E95]" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5 text-[#7D8E95]" />
                                            )}
                                        </div>
                                    </div>

                                    {/* Expanded Editor */}
                                    {isExpanded && (
                                        <div className="border-t border-[#FFBB98] p-4 bg-[#FEF6EF] space-y-4">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-[#2A8585] mb-1">
                                                        Icon
                                                    </label>
                                                    <select
                                                        value={topic.icon}
                                                        onChange={(e) => updateTopic(topic.id, 'icon', e.target.value)}
                                                        className="w-full px-3 py-2 border border-[#FFBB98] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30"
                                                    >
                                                        {ICON_OPTIONS.map(icon => (
                                                            <option key={icon} value={icon}>{icon}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-[#2A8585] mb-1">
                                                        Color Variant
                                                    </label>
                                                    <select
                                                        value={topic.variant}
                                                        onChange={(e) => updateTopic(topic.id, 'variant', e.target.value)}
                                                        className="w-full px-3 py-2 border border-[#FFBB98] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30"
                                                    >
                                                        {VARIANT_OPTIONS.map(v => (
                                                            <option key={v.value} value={v.value}>{v.label}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="flex items-end">
                                                    <button
                                                        onClick={() => updateTopic(topic.id, 'is_visible', !topic.is_visible)}
                                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
                                                            topic.is_visible 
                                                                ? 'bg-[#7D8E95]/20 border-[#1D6B6B] text-[#145555]' 
                                                                : 'bg-[#FFBB98]/20 border-[#FFBB98] text-[#7D8E95]'
                                                        }`}
                                                    >
                                                        {topic.is_visible ? (
                                                            <><Eye className="w-4 h-4" /> Visible</>
                                                        ) : (
                                                            <><EyeOff className="w-4 h-4" /> Hidden</>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-[#2A8585] mb-1">
                                                    Title
                                                </label>
                                                <input
                                                    type="text"
                                                    value={topic.title}
                                                    onChange={(e) => updateTopic(topic.id, 'title', e.target.value)}
                                                    className="w-full px-3 py-2 border border-[#FFBB98] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-[#2A8585] mb-1">
                                                    Short Description
                                                </label>
                                                <input
                                                    type="text"
                                                    value={topic.shortAnswer}
                                                    onChange={(e) => updateTopic(topic.id, 'shortAnswer', e.target.value)}
                                                    className="w-full px-3 py-2 border border-[#FFBB98] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-[#2A8585] mb-1">
                                                    Full Content
                                                </label>
                                                <textarea
                                                    value={topic.fullContent}
                                                    onChange={(e) => updateTopic(topic.id, 'fullContent', e.target.value)}
                                                    rows={10}
                                                    className="w-full px-3 py-2 border border-[#FFBB98] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30 font-mono text-sm"
                                                />
                                                <p className="text-xs text-[#7D8E95] mt-1">
                                                    Use bullet points (•) and line breaks for formatting
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
                <button
                    onClick={saveContent}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-3 bg-[#1D6B6B] text-white rounded-xl hover:bg-[#1D6B6B] disabled:opacity-50 transition-colors"
                >
                    <Save className="w-5 h-5" />
                    {saving ? 'Saving...' : 'Save All Changes'}
                </button>
            </div>
        </div>
    );
};

export default EducationTab;
